/**
 * Etapa 4 — Apreciação de risco: só um resumo, com atalho para a tela do HRN.
 */
import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { DataTable } from '../../../../../components';
import { machineService, MachineHazardAssessment } from '../../../../../services/machineService';
import { errorMessage } from '../machineWizardValidation';
import { formatHrnNumber } from '../../HazardAssessment';

interface AppraisalStepProps {
  machineId: string;
  canEdit: boolean;
}

export const AppraisalStep: React.FC<AppraisalStepProps> = ({ machineId, canEdit }) => {
  const [hazards, setHazards] = useState<MachineHazardAssessment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    machineService
      .getHazardAssessments(machineId)
      .then((result) => {
        if (active) setHazards(result);
      })
      .catch((err: unknown) => {
        if (active) setError(errorMessage(err, 'Não foi possível carregar os perigos avaliados.'));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [machineId]);

  return (
    <Box>
      <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
        Apreciação de risco
      </Typography>
      <Typography variant="body1" sx={{ mb: 3 }}>
        A apreciação tem tela própria, onde você avalia cada perigo da máquina. Aqui está o resumo do que já foi
        avaliado.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <DataTable<MachineHazardAssessment>
        embedded
        loading={loading}
        rows={hazards}
        getRowId={(row) => row.id}
        emptyState={{
          title: 'Nenhum perigo avaliado ainda',
          description:
            'Sem a apreciação, a máquina não entra pronta no laudo. Você pode concluir o cadastro agora e voltar depois.',
        }}
        columns={[
          { id: 'hazard', label: 'Perigo', primary: true, render: (row) => row.hazard },
          {
            id: 'hrn',
            label: 'HRN hoje',
            align: 'right',
            render: (row) => <Chip variant="outlined" label={`${formatHrnNumber(row.hrn)} · ${row.hrnLevel}`} />,
          },
          {
            id: 'residual',
            label: 'HRN após adequações',
            align: 'right',
            render: (row) =>
              row.residualHrn === null ? (
                'Ainda não informado'
              ) : (
                <Chip variant="outlined" label={`${formatHrnNumber(row.residualHrn)} · ${row.residualLevel ?? ''}`} />
              ),
          },
        ]}
      />

      <Button
        component={RouterLink}
        to={`/machines/${machineId}/hazards`}
        variant="outlined"
        sx={{ mt: 2, minHeight: 44 }}
      >
        {canEdit ? 'Abrir a apreciação de risco' : 'Ver a apreciação de risco'}
      </Button>
    </Box>
  );
};

export default AppraisalStep;
